import React from "react";
import { Box, Drawer, Typography, alpha } from "@mui/material";
import {
  AddRounded as AddIcon,
  LocalPharmacy as PharmacyIcon,
} from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, SearchField } from "../../../atoms";
import FormTextField from "../../../atoms/FormTextField";
import FormSelect from "../../../atoms/FormSelect";
import DrawerField from "../../../molecules/DrawerField";
import { tileInputProps } from "../../../molecules/InfoTile";
import { useClinicTheme } from "../../../../theme";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import usePagedSearch from "./usePagedSearch";
import PagedOptionList from "./PagedOptionList";

const PERIOD_UNITS = [
  { value: "Gün", label: "Gün" },
  { value: "Hafta", label: "Hafta" },
  { value: "Ay", label: "Ay" },
];

/**
 * Tedaviye ilaç eklemek için sağdan açılan drawer: önce ilaç aranıp seçilir
 * (sunucuda sayfalı arama), sonra kullanım bilgileri girilir. Form durumu
 * çağırandadır (TreatmentEditor `medicineForm`); drawer yalnız gösterir.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {({ query, page }) => Promise<{ items, hasMore }>} fetchPage - `raw` ilaç kaydı
 * @param {Set<string>} existingIds - Tedavide zaten olan ilaç id'leri
 * @param {object} form - { medicine, dose1, dose2, box, quantity, usagePeriodValue, usagePeriodUnit, usageMethod }
 * @param {(medicine: object|null) => void} onSelectMedicine - null = seçimi kaldır
 * @param {(patch: object) => void} onFormChange
 * @param {() => void} onSubmit
 * @param {boolean} canSubmit
 * @param {string} [title="İlaç Ekle"] - Düzenlemede "İlacı Düzenle"
 * @param {string} [submitLabel="İlaç Ekle"] - Düzenlemede "Kaydet"
 * @param {boolean} [editing=false] - Mevcut ilacı düzenleme (buton ikonu kaydet)
 */
export default function MedicationPickerDrawer({
  open,
  onClose,
  fetchPage,
  existingIds,
  form,
  onSelectMedicine,
  onFormChange,
  onSubmit,
  canSubmit,
  title = "İlaç Ekle",
  submitLabel = "İlaç Ekle",
  editing = false,
}) {
  const { palette } = useClinicTheme();
  const tone = palette?.primary || COLORS.slate700;
  const search = usePagedSearch(open && !form.medicine, fetchPage);
  const medicine = form.medicine;

  const numberField = (key, label, required) => (
    <FormTextField
      palette={palette}
      variant="standard"
      size="small"
      type="number"
      fullWidth
      placeholder={required ? "Zorunlu" : "—"}
      value={form[key]}
      onChange={(e) => onFormChange({ [key]: e.target.value })}
      inputProps={{ min: 0, "aria-label": label }}
      InputProps={tileInputProps(true)}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<PharmacyIcon />}
        title={title}
        subtitle={
          medicine ? "Kullanım bilgilerini girin" : "Önce ilacı arayıp seçin"
        }
        onClose={onClose}
      />

      {!medicine ? (
        <>
          <Box sx={{ px: 3, pt: 2, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate100}` }}>
            <SearchField
              value={search.query}
              onChange={search.setQuery}
              placeholder="İlaç adı ara"
              isPending={search.searchPending}
              fullWidth
            />
          </Box>
          <PagedOptionList
            search={search}
            icon={<PharmacyIcon />}
            noun="ilaç"
            isSelected={() => false}
            isAdded={(o) => existingIds.has(String(o.key))}
            onToggle={(o) => onSelectMedicine(o.raw)}
          />
        </>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
          {/* Seçilen ilaç */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: alpha(tone, 0.05),
              border: `1px solid ${alpha(tone, 0.18)}`,
            }}
          >
            <PharmacyIcon sx={{ fontSize: 20, color: tone, flexShrink: 0 }} />
            <Typography
              sx={{ flex: 1, minWidth: 0, fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}
            >
              {medicine.name}
            </Typography>
            <Typography
              role="button"
              tabIndex={0}
              onClick={() => onSelectMedicine(null)}
              onKeyDown={(e) => (e.key === "Enter" ? onSelectMedicine(null) : null)}
              sx={{ fontSize: TYPO.label, fontWeight: 600, color: tone, cursor: "pointer", flexShrink: 0 }}
            >
              Değiştir
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 2.5, columnGap: 2 }}>
            <DrawerField label="Doz 1">
              {numberField("dose1", "Doz 1")}
            </DrawerField>
            <DrawerField label="Doz 2">
              {numberField("dose2", "Doz 2")}
            </DrawerField>
            <DrawerField label="Kutu *">
              {numberField("box", "Kutu", true)}
            </DrawerField>
            <DrawerField label="Adet *">
              {numberField("quantity", "Adet", true)}
            </DrawerField>
            <DrawerField label="Periyot">
              {numberField("usagePeriodValue", "Periyot")}
            </DrawerField>
            <DrawerField label="Birim">
              <FormSelect
                value={form.usagePeriodUnit}
                onChange={(v) => onFormChange({ usagePeriodUnit: v })}
                options={PERIOD_UNITS}
                placeholder=""
                variant="standard"
                size="small"
                fullWidth
                InputProps={tileInputProps(true)}
              />
            </DrawerField>
            <DrawerField label="Kullanım şekli" fullWidth>
              <FormTextField
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                placeholder="Ör. Tok karnına"
                value={form.usageMethod}
                onChange={(e) => onFormChange({ usageMethod: e.target.value })}
                InputProps={tileInputProps(true)}
              />
            </DrawerField>
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: TYPO.label, color: COLORS.slate500 }}>
            Kutu ve adet zorunludur; diğer alanlar ilacın varsayılanlarıyla gelir.
          </Typography>
        </Box>
      )}

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!medicine || !canSubmit}
        startIcon={editing ? undefined : <AddIcon />}
        fullWidth
      />
    </Drawer>
  );
}
